import { TaxonomyFacet, TaxonomyTermStatus } from '@edgebox/sync-core-data-definitions';
import { useEffect, useMemo, useState } from 'react';
import { VISIBLE_MENTION_FACETS } from '../GovernanceAnalyticsAiAnswers.utils';
import { getCachedTaxonomyTermsByFacet, ITaxonomyTermFacetSource } from '../Onboarding/ContentInventory/Components/useCachedTaxonomyData';

/** What a surface needs to render a tracked entity it only holds the term key of. */
export interface EntityTermInfo {
  name: string;
  facet: TaxonomyFacet;
  status?: TaxonomyTermStatus;
}

export interface EntityTermIndex {
  byTermKey: Map<string, EntityTermInfo>;
  /** False until the terms have arrived. Chips skeleton or hold until then, never show a key. */
  ready: boolean;
}

const EMPTY: EntityTermIndex = { byTermKey: new Map(), ready: false };

/**
 * The tracked brands, products and programs, indexed by term key.
 *
 * The sources statistics credit citations to entities by KEY alone — the response carries no name
 * and no facet — so a surface that wants to say "this source backs You, and that one backs a
 * rival" has to resolve both here. Reading them from the shared per-facet cache costs one list per
 * facet for the whole session, and archived terms are excluded at the fetch, so a retired brand
 * simply stops appearing rather than being rendered as an identifier.
 *
 * The facet list is the same one the answers list shows mentions for, so the two screens agree on
 * which entities count as brands worth naming.
 */
export function useEntityTermIndex(api?: ITaxonomyTermFacetSource): EntityTermIndex {
  const [terms, setTerms] = useState<{ key?: unknown; name?: unknown; status?: TaxonomyTermStatus; facet: TaxonomyFacet }[] | undefined>(
    undefined
  );

  useEffect(() => {
    if (!api) {
      return;
    }

    let active = true;
    void Promise.all(
      VISIBLE_MENTION_FACETS.map(async (facet) => {
        const loaded = await getCachedTaxonomyTermsByFacet(api, facet).catch(() => []);
        return (loaded as { key?: unknown; name?: unknown; status?: TaxonomyTermStatus }[]).map((term) => ({ ...term, facet }));
      })
    ).then((byFacet) => {
      if (active) {
        setTerms(byFacet.flat());
      }
    });

    return () => {
      active = false;
    };
  }, [api]);

  return useMemo(() => {
    if (!terms) {
      return EMPTY;
    }

    const byTermKey = new Map<string, EntityTermInfo>();
    for (const term of terms) {
      const key = String(term?.key ?? '').trim();
      const name = String(term?.name ?? '').trim();
      // A term with no name cannot be rendered as anything but its key, so it is not indexed at
      // all — the caller then drops the chip rather than showing an identifier.
      if (key && name && !byTermKey.has(key)) {
        byTermKey.set(key, { name, facet: term.facet, status: term.status });
      }
    }

    return { byTermKey, ready: true };
  }, [terms]);
}
